import { eq } from "drizzle-orm";
import {
  customer,
  loyalty_movement,
  order,
  type PowerSyncDrizzleDB,
  promotion,
} from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";
import type { PromotionKind } from "./calc";

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`: a
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). Mutations **can throw**: the caller
 * caller must propagate it, never swallow it.
 */

// ─── Clientes ─────────────────────────────────────────────────────────────────

/** Creates customer. `points` is balance derived server-side (not written here). */
export async function createCustomerMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { phone?: string; email?: string },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(customer).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    phone: opts?.phone?.trim() || null,
    email: opts?.email?.trim() || null,
    points: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteCustomerMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, customer, customer.id, id, { deleted_at: nowISO() }, options);
}

/**
 * Associates customer with order (to accrue points on checkout). CC-12:
 * forwards branch_id on every `order` update.
 */
export async function setOrderCustomerMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  customerId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    { customer_id: customerId, branch_id: current[0]?.branch_id ?? null },
    options,
  );
}

// ─── Loyalty (points redemption) ─────────────────────────────────────────────

export interface RedeemPointsResult {
  /** Movimiento de canje creado (puntos negativos). */
  movementId: string;
  /** Discount applied to order, in cents. */
  discountCents: number;
}

/**
 * Redeems customer's `pointsToRedeem` points as order discount.
 *
 * Writes `loyalty_movement` reason='redeem' (negative points): balance
 * customer recalculated by server-side write-path idempotently. Applies
 * discount to `discount` of order and registers `points_redeemed`. Calculation
 * de puntos→centavos es PURO (calc.ts). CC-7 (await) + CC-12 (branch_id).
 */
export async function redeemPointsForOrderMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  customerId: string,
  pointsToRedeem: number,
  discountCents: number,
  options?: VerifiedWriteOptions,
): Promise<RedeemPointsResult> {
  const points = Math.max(0, Math.trunc(pointsToRedeem));
  const discount = Math.max(0, Math.trunc(discountCents));
  const now = nowISO();

  const movementId = generateId();
  await db.insert(loyalty_movement).values({
    id: movementId,
    organization_id: organizationId,
    customer_id: customerId,
    order_id: orderId,
    points_delta: -points,
    reason: "redeem",
    created_at: now,
    updated_at: now,
  });

  // Applies redemption discount to order (added to existing discount,
  // capped at subtotal). CC-12: forwards branch_id.
  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const subtotal = current[0]?.subtotal ?? 0;
  const tax = current[0]?.tax_total ?? 0;
  const newDiscount = Math.min(discount, subtotal);
  const newTotal = Math.max(0, subtotal - newDiscount + tax);
  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    {
      customer_id: customerId,
      discount: newDiscount,
      total: newTotal,
      points_redeemed: points,
      branch_id: current[0]?.branch_id ?? null,
    },
    options,
  );

  return { movementId, discountCents: newDiscount };
}

// ─── Promociones ──────────────────────────────────────────────────────────────

export async function createPromotionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  kind: PromotionKind,
  value: number,
  opts?: { startHour?: number | null; endHour?: number | null },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(promotion).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    kind,
    value: Math.trunc(value),
    start_hour: opts?.startHour ?? null,
    end_hour: opts?.endHour ?? null,
    is_active: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deletePromotionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, promotion, promotion.id, id, { deleted_at: nowISO() }, options);
}
